import { BRANDCLUB_BRAND_ID } from './constants';
import { isParentProduct } from './stringUtils';

const BRANDCLUB_LOGO_URL = 'https://media.brandclub.com/brandclub/image_asset/brandclub-b-logo.webp';

export const BC_MEDIA_DOMAIN = 'https://media.brandclub.com';

export const BC_VIDEOS = {
  HOME_HEAD: {
    video: 'https://media.brandclub.com/brandclub/media/home_head.mp4',
    coverImage: 'https://media.brandclub.com/brandclub/media/cover/home_head.png',
  },
  DAVID: {
    video: 'https://media.brandclub.com/brandclub/media/video_david.mp4',
    coverImage: 'https://media.brandclub.com/brandclub/media/cover/video_david.png',
  },
  JESSICA: {
    video: 'https://media.brandclub.com/brandclub/media/video_jessica.mp4',
    coverImage: 'https://media.brandclub.com/brandclub/media/cover/video_jessica.png',
  },
  EMMA: {
    video: 'https://media.brandclub.com/brandclub/media/video_emma.mp4',
    coverImage: 'https://media.brandclub.com/brandclub/media/cover/video_emma.png',
  },
  ANGELA: {
    video: 'https://media.brandclub.com/brandclub/media/video_angela.mp4',
    coverImage: 'https://media.brandclub.com/brandclub/media/cover/video_angela.png',
  },
  MARCUS: {
    video: 'https://media.brandclub.com/brandclub/media/video_marcus.mp4',
    coverImage: 'https://media.brandclub.com/brandclub/media/cover/video_marcus.png',
  },
} as const;

export const BC_TESTIMONIALS = [
  {
    ...BC_VIDEOS.DAVID,
    name: 'David R.',
    location: 'Minneapolis, MN',
    testimonial: `“I can now earn cash back for purchases at Amazon, Walmart and Target.”`,
  },
  {
    ...BC_VIDEOS.JESSICA,
    name: 'Jessica P.',
    location: 'Dallas, TX',
    testimonial: `“I can earn cash back whenever I shop online or in my favorite stores.”`,
  },
  {
    ...BC_VIDEOS.EMMA,
    name: 'Emma S.',
    location: 'Phoenix, AZ',
    testimonial: `“Brandclub has an amazing affiliate program where I can earn up to 20% commission.”`,
  },
  {
    ...BC_VIDEOS.ANGELA,
    name: 'Angela W.',
    location: 'Chicago, Il.',
    testimonial: `“Brandclub allows me to earn cash back for the items that are always in my shopping cart.”`,
  },
  {
    ...BC_VIDEOS.MARCUS,
    name: 'Marcus H.',
    location: 'Boston, MA',
    testimonial: `“I can easily compare prices across Chewy, Amazon and other retailers.”`,
  },
];

export const getImageAssetUrl = (
  fileName: string,
  relativePath = 'brandclub/image_asset',
  domain = BC_MEDIA_DOMAIN,
): string => {
  return `${domain}/${relativePath}/${fileName}`;
};

export const getAssetNameFromUrl = (fileUrl: string) => {
  const arr = fileUrl.split('/');
  if (arr.length > 0) {
    return arr[arr.length - 1];
  }
  return '';
};

export const getFeaturedRetailerImage = (id: number | string) => {
  return `https://media.brandclub.com/retailer_logo/white/${id}.webp`;
};

const RETAILER_LOGO_FALLBACK_URL = (id: number | string, subId?: number | string) =>
  `https://media.brandclub.com/retailer_logo/${subId ?? id}.webp`;

export const getEntityImage = (
  id: number | string,
  type: 'brand' | 'product' | 'retailer',
  subId?: number | string,
  retailerShortLogo?: string,
) => {
  if (type === 'retailer') {
    return retailerShortLogo ?? RETAILER_LOGO_FALLBACK_URL(id, subId);
  }

  if (type === 'brand' && +id === 50000) {
    return BRANDCLUB_LOGO_URL;
  }

  if (isParentProduct(`${id}`)) {
    return `https://s3-us-west-2.amazonaws.com/stackline-product-images/parent-products/${id}.jpg`;
  }

  return `https://s3-us-west-2.amazonaws.com/stackline-product-images/${id}.jpg`;
};

export const getDefaultBrandclubImage = (brandId: number) => {
  return `https://media.brandclub.com/b/428x428/${brandId}.jpg`;
};

export const getFallbackBrandclubImage = (brandId: number) => {
  return `https://media-api.brandclub.com/b/428x428/${brandId}.jpg`;
};

export const getDefaultProductImage = (stacklineSku: string) => {
  return `https://media.brandclub.com/p/428x428/${
    isParentProduct(stacklineSku) ? 'parent-products/' : ''
  }${stacklineSku}.jpg`;
};

export const getFallbackProductImage = (stacklineSku: string) => {
  return `https://media-api.brandclub.com/p/428x428/${
    isParentProduct(stacklineSku) ? 'parent-products/' : ''
  }${stacklineSku}.jpg`;
};

/**
 * Resolve product image URLs from metadata first, falling back to generated CDN URLs.
 *
 * Priority:
 *  1. product.imageUrls[0] (preferred — from API response)
 *  2. getDefaultProductImage(stacklineSku)  (media.brandclub.com)
 *  3. getFallbackProductImage(stacklineSku) (media-api.brandclub.com)
 *
 * Returns { src, fallbackImage } matching <FallbackImage /> props for easy spreading.
 */
export const resolveProductImageUrls = (product: {
  stacklineSku: string;
  imageUrls?: string[];
}): { src: string; fallbackImage: string } => {
  const { stacklineSku } = product;
  const metadataUrl = product.imageUrls?.[0];

  if (metadataUrl) {
    return {
      src: metadataUrl,
      fallbackImage: getDefaultProductImage(stacklineSku),
    };
  }

  return {
    src: getDefaultProductImage(stacklineSku),
    fallbackImage: getFallbackProductImage(stacklineSku),
  };
};

export const getBrandLogo = (brandId: number | string) => {
  if (brandId === BRANDCLUB_BRAND_ID) {
    return getImageAssetUrl(`${brandId}.svg`, 'b/428x428');
  }
  return getImageAssetUrl(`${brandId}.jpg`, 'b/428x428');
};
